import React, { useState, KeyboardEvent, useRef } from "react";

export interface MultiValueInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  separator?: "," | " " | string;
}

export const MultiValueInput: React.FC<MultiValueInputProps> = ({
  values = [],
  onChange,
  placeholder = "Type and press space or comma to add",
  className = "",
  disabled = false,
  separator = ",",
}) => {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    // Handle Enter, or the separator to add value
    // Only handle Space if separator is not "," (to allow spaces within comma-separated tags)
    const shouldAddOnSpace = separator !== ",";
    if (e.key === "Enter" || e.key === separator || (shouldAddOnSpace && e.key === " ")) {
      e.preventDefault();
      e.stopPropagation();
      addValue();
    }

    // Handle Backspace to remove last value if input is empty
    if (e.key === "Backspace" && inputValue === "" && values.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      removeValue(values.length - 1);
    }
  };

  const addValue = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
      setInputValue("");
    }
  };

  const removeValue = (index: number) => {
    const newValues = values.filter((_, i) => i !== index);
    onChange(newValues);
  };

  const handleBlur = () => {
    // Add value on blur if there's input
    if (inputValue.trim()) {
      addValue();
    }
  };

  return (
    <div className={`flex flex-wrap gap-2 p-2 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 ${className}`}>
      {/* Value badges */}
      {values.map((value, index) => (
        <span
          key={index}
          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded"
        >
          {value}
          {!disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                removeValue(index);
              }}
              className="hover:bg-blue-200 p-0.5 rounded transition-colors"
              aria-label={`Remove ${value}`}
            >
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </span>
      ))}

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={values.length === 0 ? placeholder : ""}
        disabled={disabled}
        className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
      />
    </div>
  );
};

export default MultiValueInput;

